import { useMutation, useQuery, useQueryClient, skipToken } from '@tanstack/react-query';
import { getBrand, listBrands, saveSender, saveTopic, updateBrand } from '@/lib/brands-api';
import type { SaveSenderInput, SaveTopicInput, UpdateBrandInput } from '@/lib/brands-api';

export const brandKeys = {
  list: () => ['brands'] as const,
  detail: (id: string) => ['brand', id] as const,
};

export function useBrands() {
  return useQuery({ queryKey: brandKeys.list(), queryFn: listBrands });
}

export function useBrand(brandId: string | undefined) {
  return useQuery({
    queryKey: brandKeys.detail(brandId ?? 'none'),
    queryFn: brandId === undefined ? skipToken : () => getBrand(brandId),
  });
}

/**
 * Deliberately NOT optimistic.
 *
 * Changing the from-domain resets the brand's authentication server-side, so the response carries
 * consequences the client cannot predict — an optimistic update would show a green "authenticated"
 * badge for the moment between the click and the reply, on the one field where a wrong answer means
 * sending unsigned mail.
 */
export function useUpdateBrand(brandId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateBrandInput) => updateBrand(brandId, input),
    onSuccess: async (brand) => {
      client.setQueryData(brandKeys.detail(brandId), brand);

      await client.invalidateQueries({ queryKey: brandKeys.list() });
    },
  });
}

export function useSaveTopic(brandId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: { topicId: string | null; values: SaveTopicInput }) =>
      saveTopic(brandId, input.topicId, input.values),

    onSuccess: () => {
      // The brand carries its topics, and the campaign audience picker reads them from there.
      void client.invalidateQueries({ queryKey: brandKeys.detail(brandId) });
      void client.invalidateQueries({ queryKey: brandKeys.list() });
    },
  });
}

export function useSaveSender(brandId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: SaveSenderInput & { senderIdentityId?: string }) =>
      saveSender({ brandId, ...input }),
    onSuccess: async () => {
      // The detail query owns the sender list, so it is refetched rather than patched — the server
      // computes each address from the brand's domain and guessing it here would be a second copy of
      // that rule.
      await client.invalidateQueries({ queryKey: brandKeys.detail(brandId) });
    },
  });
}
